import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { ResponseErrorDto, MeUserDto } from '@interfaces/models';
import { transferAdminPlusRole, getUsers } from '@api/users';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { z } from 'zod';

const permissionTransferSchema = (t: TFunction) =>
  z.object({
    id: z.string({ required_error: t('FORMS.REQUIRED') }),
  });

const toUserOptions = (data: Record<'firstName' | 'lastName' | 'email' | 'id', string>) => ({
  label: `${data.firstName} ${data.lastName} (${data.email})`,
  value: data.id,
  keywords: [data.firstName, data.lastName, data.email],
});

type CurrentUserPermissionTransferForm = z.infer<ReturnType<typeof permissionTransferSchema>>;

export const useCurrentUserPermissionTransfer = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const { setUser } = useCurrentUser();
  const { user } = useCurrentUser();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(permissionTransferSchema(t)),
    defaultValues: { id: undefined },
  });

  const {
    data: users,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const {
    mutate: transferRole,
    isPending: isTransferRolePending,
    isSuccess: isTransferRoleSuccess,
    isError: isTransferRoleError,
  } = useMutation({
    mutationFn: transferAdminPlusRole,
    onSuccess: (user: MeUserDto) => {
      toast.success(t('CURRENT_USER_EDIT.ACCOUNT_OWNER_SUCCESS'));
      setUser(user);
      setSubmitDialogOpen(false);
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);

  const userOptions = useMemo(() => {
    // Filter out current user. Current user can't select himself
    return users?.filter((option) => option.id !== user?.id)?.map(toUserOptions) ?? [];
  }, [users, user]);

  const selectedUserId = form.watch('id');

  const selectedUser = useMemo(
    () => users?.find((user) => user.id === selectedUserId),
    [selectedUserId, users],
  );

  const onSubmit = (value: CurrentUserPermissionTransferForm) => {
    transferRole(value.id);
  };

  return {
    isTransferRolePending,
    isTransferRoleSuccess,
    isTransferRoleError,
    submitDialogOpen,
    selectedUser,
    userOptions,
    isLoading,
    isError,
    form,
    t,
    setSubmitDialogOpen,
    onSubmit,
  };
};
